<template>
  <v-container class="ai-agent">
    <p class="text-center title mt-6">ШІ-агент у боті підтримки</p>
    <p class="text-center grey--text">
      Відповідає психологам у Telegram-боті Platforma.bot за базою знань нижче. Ця ж база працює в новому боті Платформи, його чати в CRM, вкладка «Чати бота». Питання про оплати, активацію анкети, помилки сайту передає команді.
    </p>
    <p v-if="loaded && !configured" class="text-center red--text">Ключ OpenAI на сервері не задано — агент не відповідатиме.</p>

    <v-card outlined class="pa-4 mt-4">
      <p class="font-weight-bold mb-1">Кому відповідає в боті</p>
      <v-radio-group v-model="mode" class="mt-0" hide-details>
        <v-radio value="off" label="Нікому (вимкнено)"></v-radio>
        <v-radio value="test" :label="`Лише тестовим контактам — з тегом «${testTag}» у SendPulse`"></v-radio>
        <v-radio value="all" label="Усім психологам у боті"></v-radio>
      </v-radio-group>
      <p class="caption grey--text mt-2 mb-0">
        Тег додається в SendPulse: Чати → відкрити свій чат з ботом → Теги. Якщо в чаті щойно писав хтось із команди, агент там мовчить {{ pauseHint }}.
      </p>
    </v-card>

    <v-card outlined class="pa-4 mt-4">
      <p class="font-weight-bold mb-1">База знань</p>
      <p class="caption grey--text">
        Текст, з якого агент бере відповіді. Зараз тут інструкція з сайту бази знань Платформи{{ knowledgeIsDefault ? "" : " (змінена вами)" }}. Допишіть те, про що часто питають: ціни, оплату, реєстрацію, контакти.
      </p>
      <v-textarea v-model="knowledge" outlined rows="14" hide-details class="ai-agent__knowledge"></v-textarea>
      <div class="d-flex flex-wrap ai-agent__row mt-3">
        <v-btn color="primary" depressed :loading="saving" @click="save">Зберегти</v-btn>
      </div>
    </v-card>

    <v-card outlined class="pa-4 mt-4">
      <p class="font-weight-bold mb-1">Перевірити агента</p>
      <p class="caption grey--text">Пишіть як психолог. Нічого не йде в бот, агент бере збережену базу знань.</p>
      <div class="ai-agent__chat">
        <p v-if="!chat.length" class="grey--text mb-0">Наприклад: «Як підключити Google Календар?»</p>
        <div
          v-for="(item, index) in chat"
          :key="index"
          class="ai-agent__msg"
          :class="item.role === 'user' ? 'ai-agent__msg--user' : 'ai-agent__msg--bot'"
        >
          <div class="ai-agent__bubble">{{ item.text || "— агент промовчить (бот сам відповість кнопками)" }}</div>
          <div v-if="item.handoff" class="caption orange--text">передано команді</div>
        </div>
      </div>
      <div class="d-flex ai-agent__row mt-3">
        <v-textarea
          v-model="question"
          placeholder="Ваше питання"
          outlined
          dense
          auto-grow
          rows="1"
          hide-details
          class="ai-agent__input"
          @keydown.enter.exact.prevent="ask"
        ></v-textarea>
        <v-btn color="primary" depressed :loading="asking" :disabled="!question.trim()" @click="ask">Надіслати</v-btn>
        <v-btn v-if="chat.length" text @click="chat = []">Очистити</v-btn>
      </div>
    </v-card>

    <p class="font-weight-bold mt-8 mb-2">Останні відповіді</p>
    <p v-if="!logs.length" class="grey--text">Відповідей ще немає</p>
    <v-card v-for="item in logs" :key="item.id" outlined class="pa-4 mb-3">
      <div class="caption grey--text">
        {{ dateOf(item.created_at) }} · {{ item.source === "bot" ? "бот" : "тест" }}<template v-if="item.contact_name"> · {{ item.contact_name }}</template>
        <template v-if="item.tokens"> · {{ item.tokens }} токенів</template>
      </div>
      <p class="ai-agent__text mb-1"><b>Питання:</b> {{ item.question }}</p>
      <p v-if="item.answer" class="ai-agent__text mb-1"><b>Відповідь:</b> {{ item.answer }}</p>
      <p v-else-if="!item.error" class="caption grey--text mb-1">Агент промовчав</p>
      <div class="caption">
        <span v-if="item.handoff" class="orange--text">передано команді</span>
        <span v-if="item.source === 'bot' && item.answer && !item.sent" class="red--text"> · не надіслано</span>
        <span v-if="item.error" class="red--text">{{ errorText(item.error) }}</span>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import Vue from "vue";
import ApiClient from "@/libs/http-client";

export default {
  name: "AiAgentAdmin",
  metaInfo() {
    return { title: "Платформа / ШІ-агент" };
  },
  data() {
    return {
      loaded: false,
      configured: true,
      mode: "off",
      testTag: "ai_test",
      knowledge: "",
      knowledgeIsDefault: true,
      logs: [],
      saving: false,
      chat: [],
      question: "",
      asking: false,
      pauseHint: "12 годин",
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      return ApiClient.get("api/admin/ai-agent").then((response) => {
        const data = response.data.data;
        this.mode = data.mode;
        this.testTag = data.test_tag;
        this.knowledge = data.knowledge;
        this.knowledgeIsDefault = data.knowledge_is_default;
        this.configured = data.configured;
        this.logs = data.logs;
        this.loaded = true;
      });
    },
    loadLogs() {
      return ApiClient.get("api/admin/ai-agent").then((response) => {
        this.logs = response.data.data.logs;
      });
    },
    toast(text, icon = "success") {
      Vue.swal({ target: document.getElementById("main"), text, icon, timer: 3000, toast: true, position: "top-right", showConfirmButton: false });
    },
    save() {
      this.saving = true;
      ApiClient.post("api/admin/ai-agent", { mode: this.mode, knowledge: this.knowledge })
        .then((response) => {
          if (!response) return;
          this.toast(response.data.message);
          this.load();
        })
        .finally(() => {
          this.saving = false;
        });
    },
    ask() {
      const text = this.question.trim();
      if (!text || this.asking) return;
      this.chat.push({ role: "user", text });
      this.question = "";
      this.asking = true;
      const messages = this.chat.filter((item) => item.text).map((item) => ({ role: item.role, text: item.text }));
      ApiClient.post("api/admin/ai-agent/ask", { messages })
        .then((response) => {
          if (!response) return;
          const data = response.data.data;
          this.chat.push({ role: "assistant", text: data.reply, handoff: data.handoff });
          this.loadLogs();
        })
        .catch((error) => {
          this.toast((error && error.message) || "ШІ не відповів", "error");
        })
        .finally(() => {
          this.asking = false;
        });
    },
    errorText(error) {
      if (error === "operator_active") return "у чаті нещодавно писала команда — агент не втручався";
      if (error === "menu_button") return "натиснуто кнопку бота — відповідає сам бот";
      return "Помилка: " + error;
    },
    dateOf(value) {
      return new Date(value).toLocaleString("uk-UA", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    },
  },
};
</script>

<style lang="scss" scoped>
.ai-agent {
  max-width: 900px;
}
.ai-agent__row {
  gap: 10px;
  align-items: flex-start;
}
.ai-agent__input {
  flex: 1 1 auto;
}
.ai-agent__knowledge ::v-deep textarea {
  font-size: 13px;
  line-height: 1.45;
}
.ai-agent__chat {
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  max-height: 460px;
  padding: 12px;
  overflow-y: auto;
  background: #F4F7F9;
  border-radius: 8px;
}
.ai-agent__msg {
  display: flex;
  flex-direction: column;
  max-width: 85%;
}
.ai-agent__msg--user {
  align-self: flex-end;
  align-items: flex-end;
}
.ai-agent__msg--bot {
  align-self: flex-start;
}
.ai-agent__bubble {
  padding: 8px 12px;
  white-space: pre-line;
  overflow-wrap: anywhere;
  background: #fff;
  border-radius: 10px;
}
.ai-agent__msg--user .ai-agent__bubble {
  color: #fff;
  background: #00A3B7;
}
.ai-agent__text {
  white-space: pre-line;
  overflow-wrap: anywhere;
}
</style>
